import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { observer, inject } from 'mobx-react';

import { mergeStyleSets, ActionButton } from '@fluentui/react';

import DefaultButtonEx from '@v2/components/base/DefaultButtonEx'

import { omit, pick, asString } from 'shared/utils/utils'

import { FieldSelector } from './common';
import { Manager } from './Manager';
import * as Validation from './validation';


import * as DTU from 'src/components/base/fields/dateTimeUtils';
import * as SF from 'src/components/base/fields/salesforce';

import * as QA from 'shared/utils/queryAnalysis';


const classNames = mergeStyleSets({
  root: {
    paddingBottom: '120px',
    backgroundColor: '#F4F4F4',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  constantsContainer: {
    selectors: {
      '&> * + *': {
        marginTop: 20,
      }
    },
  },

  constant: {
    backgroundColor: 'white',
    padding: '16px',
  },

  constantHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  constantBody: {
    selectors: {
      '&> span': {
        display: 'block',
      },
      '&> * + *': {
        marginTop: '12px',
      }
    }
  },

  button: {
    margin: '12px',
  },
});


const Change = {
  Field: 'field',
  Value: 'value',
};


export const Constants = observer(({ mappingManager, pageControl, isDisabled, onChange }) => {
  const [validationResult, setValidationResult] = useState(null);
  const [constants, setConstants] = useState([]);
  const [isDirty, setDirty] = useState(false);

  const error = useMemo(() => {
    if (!validationResult) {
      return 'Validating...';
    }

    const { general, constants } = validationResult;
    return constants.length
      ? 'Some constants have errors'
      : general.length
        ? general[0].message
        : '';
  }, [validationResult]);

  useEffect(() => {
    // console.log('Mounting constants');
    loadConstants();
    // return () => { console.log('Unmounting constants'); }
  }, []);


  const debouncedValidate = useDebouncedCallback(() => validate(), 500);

  useEffect(() => {
    debouncedValidate();
  }, [constants, validationResult]);

  useEffect(() => {
    const commands = isDisabled
      ? []
      : [
        { key: 'reset', iconProps: { iconName: 'Clear' }, onClick: handleResetConstants },
        { key: 'apply',
          iconProps: { iconName: !error ? 'Accept' : 'UserWarning' },
          ...(!validationResult || validationResult.hasAnyError ? { callout: error } : { onClick: handleSaveConstants })
        },
      ];
    pageControl.setHeaderCommands(commands);
  }, [error, isDisabled, constants, validationResult]);

  useEffect(() => {
    pageControl.setDirtyActions(isDirty
      ? { 
        text: 'Constants changed', 
        onSave: async () => handleSaveConstants(), 
        onForget: async () => handleForgetChanges(),
        disableSave: !!error,
      }
      : null);
  }, [isDirty, constants, error]);

  function updateConstants(updateFn, setDirtyFlag = true) {
    setConstants(prev => updateFn(prev));
    setValidationResult(null);
    setDirtyFlag && setDirty(true);
  }

  function loadConstants() {
    const c = mappingManager.loadConstants()
      .map(x => ({ ...x, fieldError: '', valueError: '', isDisabled: false }));

    updateConstants(() => c, false);
    !c.length && handleConstantAdd();
  }

  function validate() {
    if (validationResult) {
      return;
    }

    const res = mappingManager.validate({ constants }, Validation.Source.Constant);

    setValidationResult(res);
    setConstants(prevConstants => {
      return prevConstants.map(constant => {
        const { errors, isDisabled } = res.getFieldStatus(constant);
        const eType = Validation.ErrorType.ValueError;
        const valueError = errors.find(x => x.type === eType)?.message;
        const fieldError = errors.find(x => x.type !== eType)?.message;

        return { ...constant, valueError, fieldError, isDisabled };
      });
    });
  }

  function createConstant() {
    const { fieldData } = mappingManager.objectFieldStrictOptions[0];
    const { value, error: valueError = '' } = getDefaultValue(fieldData.rawField);
    const c = { 
      ...Manager.generateConstant(),
      fieldError: '', 
      valueError,
      value,
      fieldData,
      isDisabled: false,
    };

    mappingManager.addToUsedFields(c);
    return c;
  }

  function handleConstantAdd() {
    updateConstants((prevConstants) => [...prevConstants, createConstant()]);
  }

  const handleConstantChange = useCallback((newItem, change = Change.Value) => {
    updateConstants((prevConstants) => prevConstants.map(item => {
      if (item.id !== newItem.id) {
        return item;
      }
      
      if (change === Change.Field) {
        mappingManager.removeFromUsedFields(item);
        mappingManager.addToUsedFields(newItem);
      }

      return newItem;
    }));
  }, [mappingManager]);

  const handleConstantRemove = useCallback((item) => {
    mappingManager.removeFromUsedFields(item);
    updateConstants(prevConstants => prevConstants.filter(x => x.id !== item.id));
  }, [mappingManager]);

  function handleSaveConstants() {
    const c = constants.map(x => omit(x, ['fieldError', 'valueError', 'isDisabled']));
    mappingManager.saveConstants(c);
    onChange(c);
  }

  function handleForgetChanges() {
    mappingManager.forgetChanges();
  }

  function handleResetConstants() {
    handleForgetChanges();
    loadConstants();
  }

  return (
    <div className={classNames.root}>
      <div className={classNames.constantsContainer}>
        {constants.map((x, index) => 
          <Constant
            key={x.id}
            constant={x}
            mappingManager={mappingManager}
            onChange={handleConstantChange}
            onRemove={handleConstantRemove}
            isDisabled={isDisabled}
            index={index}
          />
          )}
      </div>

      { !isDisabled && 
        <div className={classNames.button} >
          <DefaultButtonEx
          text={'Add constant'}
          onClick={handleConstantAdd}
          primary={true}
          />
        </div>
      }
    </div>
  );
});



function Constant(props) {
  const { mappingManager, onChange, onRemove, constant, isDisabled: isEditingDisabled, index } = props;
  const { id, fieldError, valueError, fieldData, value, isDisabled } = constant;
  const { rawField, name, field, objectName } = fieldData ?? {};

  const isCompletelyDisabled = isDisabled || isEditingDisabled;

  function handleFieldChange(item) {
    const { fieldData } = item;
    const { value, error: valueError = '' } = getDefaultValue(fieldData.rawField);
    onChange({ ...constant, fieldData, value, valueError }, Change.Field);
  } 

  const handleValueChange = useCallback(({ value, error }) => {
    onChange({ ...constant, value, valueError: error }, Change.Value);
  }, [constant]);

  const handleRemove = useCallback(() => {
    onRemove(constant);
  }, [constant]);

  const description = useMemo(() => `Constant #${index + 1}${isDisabled ? ' (ignored)' : ''}`, [index, isDisabled]);

  return (
    <div className={classNames.constant}>

      <div className={classNames.constantHeader}>
        <span className="ms-font-l">{description}</span>
        <ActionButton iconProps={{ iconName: 'RecycleBin' }} onClick={handleRemove} disabled={isEditingDisabled}>
          Delete
        </ActionButton>
      </div>

      <div className={classNames.constantBody}>
        <FieldSelector
          placeholer={'Select field to add a constant for'}
          selectedKey={name}
          onChange={handleFieldChange}
          options={mappingManager.objectFieldStrictOptions}
          errorMessage={fieldError}
          disabled={isCompletelyDisabled}
        />

        <SF.Field
          objectName={objectName}
          rawField={rawField}
          value={value}
          error={valueError}
          showLabels={mappingManager.showLabels}
          onChange={handleValueChange}
          disabled={isCompletelyDisabled}
        />
      </div>

    </div>
  );
}


function getDefaultValue(rawField) {
  const cvt = {
    checkbox: (x) => ({ value: x.value }),
    picklist: (option) => ({ value: option?.value ?? '' }),
    simple: (value) => ({ value: asString(value) }),
    time: (value) => {
      const Ftn = QA.FieldTypeName;
      const Fmt = DTU.Format;
      const helper = {
        [Ftn.Date]: Fmt.Date,
        [Ftn.DateTime]: Fmt.DateTime,
        [Ftn.Time]: Fmt.Time,
      };
      return ({ value: DTU.momentToString(value, helper[rawField.type]) });
    },
    lookup: (value) => ({ value, error: 'Select a valid reference / id' }),
    rich: (value) => ({ value: asString(value) })
  };

  return SF.getDefaultValue(rawField, cvt);
}


export default Constants;